import React from 'react';
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { Suspense } from 'react';
import AuthRoute from '../components/AuthRoute';
import { isTenantRuntime } from '@saasplatform/config';
import RouteErrorFallback from '../components/RouteErrorFallback';
import lazyWithRetry from './lazyWithRetry';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

// 懒加载页面组件
const Layout = lazyWithRetry(() => import('../components/Layout'), 'app-layout');
const Login = lazyWithRetry(() => import('../pages/Login'), 'admin-login');
const Dashboard = lazyWithRetry(() => import('../pages/Dashboard'), 'admin-dashboard');
const Tenants = lazyWithRetry(() => import('../pages/Tenants'), 'admin-tenants');
const Users = lazyWithRetry(() => import('../pages/Users'), 'admin-users');
const Roles = lazyWithRetry(() => import('../pages/Roles'), 'admin-roles');
const Permissions = lazyWithRetry(() => import('../pages/Permissions'), 'admin-permissions');
const SslCertificates = lazyWithRetry(() => import('../pages/SslCertificates'), 'admin-ssl-certificates');
const SmsRecords = lazyWithRetry(() => import('../pages/SmsRecords'), 'admin-sms-records');
const SmsConfig = lazyWithRetry(() => import('../pages/SmsConfig'), 'admin-sms-config');
const CaptchaConfig = lazyWithRetry(() => import('../pages/CaptchaConfig'), 'admin-captcha-config');
const WechatOpenPlatformConfig = lazyWithRetry(() => import('../pages/WechatOpenPlatformConfig/index'), 'admin-wechat-open-platform-config');
const WechatOpenPlatformAuthorize = lazyWithRetry(() => import('../pages/WechatOpenPlatformAuthorize/index'), 'admin-wechat-open-platform-authorize');
const PlatformWebsiteConfig = lazyWithRetry(() => import('../pages/PlatformWebsiteConfig/index'), 'admin-platform-website-config');

const TenantLogin = lazyWithRetry(() => import('../pages/Tenant/Login'), 'tenant-login');
const TenantRegister = lazyWithRetry(() => import('../pages/Tenant/Register'), 'tenant-register');
const TenantOneClickLogin = lazyWithRetry(() => import('../pages/Tenant/OneClickLogin'), 'tenant-one-click-login');
const TenantDashboard = lazyWithRetry(() => import('../pages/Tenant/Dashboard'), 'tenant-dashboard');
const TenantMarketing = lazyWithRetry(() => import('../pages/Tenant/Marketing'), 'tenant-marketing');
const TenantWebsiteConfig = lazyWithRetry(() => import('../pages/Tenant/WebsiteConfig/index'), 'tenant-website-config');
const TenantSeoConfig = lazyWithRetry(() => import('../pages/Tenant/SeoConfig/index'), 'tenant-seo-config');
const TenantProfile = lazyWithRetry(() => import('../pages/Tenant/Profile'), 'tenant-profile');
const TenantUsers = lazyWithRetry(() => import('../pages/Tenant/SubUsers'), 'tenant-users');
const TenantRoles = lazyWithRetry(() => import('../pages/Tenant/Roles'), 'tenant-roles');
const TenantArticles = lazyWithRetry(() => import('../pages/Tenant/Articles/index'), 'tenant-articles');
const TenantComments = lazyWithRetry(() => import('../pages/Tenant/Comments/index'), 'tenant-comments');
const TenantMiniUsers = lazyWithRetry(() => import('../pages/Tenant/MiniUsers/index'), 'tenant-mini-users');
const TenantArticleEditor = lazyWithRetry(() => import('../pages/Tenant/Articles/EditorPage'), 'tenant-article-editor');
const TenantCategories = lazyWithRetry(() => import('../pages/Tenant/Categories/index'), 'tenant-categories');
const TenantBanners = lazyWithRetry(() => import('../pages/Tenant/Banners/index'), 'tenant-banners');
const TenantTitleLibrary = lazyWithRetry(() => import('../pages/Tenant/TitleLibrary/index'), 'tenant-title-library');
const TenantAutoWritingWorkflows = lazyWithRetry(() => import('../pages/Tenant/AutoWritingWorkflows/index'), 'tenant-auto-writing-workflows');
const TenantWritingStats = lazyWithRetry(() => import('../pages/Tenant/WritingStats/index'), 'tenant-writing-stats');
const TenantMiniProgramDev = lazyWithRetry(() => import('../pages/Tenant/MiniProgramDev/index'), 'tenant-mini-program-dev');
const TenantAdConfig = lazyWithRetry(() => import('../pages/Tenant/AdConfig/index'), 'tenant-ad-config');
const TenantWechatPayConfig = lazyWithRetry(() => import('../pages/Tenant/WechatPayConfig/index'), 'tenant-wechat-pay-config');
const TenantAlipayConfig = lazyWithRetry(() => import('../pages/Tenant/AlipayConfig/index'), 'tenant-alipay-config');
const TenantQiniuConfig = lazyWithRetry(() => import('../pages/Tenant/QiniuConfig/index'), 'tenant-qiniu-config');
const TenantDeepSeekConfig = lazyWithRetry(() => import('../pages/Tenant/DeepSeekConfig/index'), 'tenant-deepseek-config');
const AdminMarketing = lazyWithRetry(() => import('../pages/MarketingAdmin/index'), 'admin-marketing');

const routeStatusStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f5f7fb',
  color: '#64748b',
};

const RouteStatus: React.FC<{ message?: string }> = ({
  message = '加载中...',
}) => (
  <div style={routeStatusStyle}>
    {message}
  </div>
);

// 加载中组件
const LoadingComponent = () => <RouteStatus />;

const isTenantFrontend = typeof window !== 'undefined' ? isTenantRuntime() : false;
const isTenantMarketingPath = (pathname: string) =>
  ['/', '/features', '/solutions', '/pricing', '/contact'].includes(pathname);
const isAdminMarketingPath = (pathname: string) => pathname === '/';

const RootShell: React.FC = () => {
  const location = useLocation();

  if (isTenantFrontend && isTenantMarketingPath(location.pathname)) {
    return (
      <Suspense fallback={<LoadingComponent />}>
        <TenantMarketing />
      </Suspense>
    );
  }

  if (!isTenantFrontend && isAdminMarketingPath(location.pathname)) {
    return (
      <Suspense fallback={<LoadingComponent />}>
        <AdminMarketing />
      </Suspense>
    );
  }

  return (
    <AuthRoute>
      <Suspense fallback={<LoadingComponent />}>
        <Layout />
      </Suspense>
    </AuthRoute>
  );
};

const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { initialized, isAuthenticated, user } = useSelector(
    (state: RootState) => state.auth,
  );

  if (!initialized) {
    return <RouteStatus message="正在恢复登录状态..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// 路由配置
const router = createBrowserRouter([
  {
    path: '/features',
    errorElement: <RouteErrorFallback />,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        {isTenantFrontend ? <TenantMarketing /> : <Navigate to="/dashboard" replace />}
      </Suspense>
    ),
  },
  {
    path: '/solutions',
    errorElement: <RouteErrorFallback />,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        {isTenantFrontend ? <TenantMarketing /> : <Navigate to="/dashboard" replace />}
      </Suspense>
    ),
  },
  {
    path: '/pricing',
    errorElement: <RouteErrorFallback />,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        {isTenantFrontend ? <TenantMarketing /> : <Navigate to="/dashboard" replace />}
      </Suspense>
    ),
  },
  {
    path: '/contact',
    errorElement: <RouteErrorFallback />,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        {isTenantFrontend ? <TenantMarketing /> : <Navigate to="/dashboard" replace />}
      </Suspense>
    ),
  },
  {
    path: '/login',
    errorElement: <RouteErrorFallback />,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        {isTenantFrontend ? <TenantLogin /> : <Login />}
      </Suspense>
    ),
  },
  {
    path: '/tenant/login',
    errorElement: <RouteErrorFallback />,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <TenantLogin />
      </Suspense>
    ),
  },
  {
    path: '/register',
    errorElement: <RouteErrorFallback />,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        {isTenantFrontend ? <TenantRegister /> : <Navigate to="/login" replace />}
      </Suspense>
    ),
  },
  {
    path: '/tenant/register',
    errorElement: <RouteErrorFallback />,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <TenantRegister />
      </Suspense>
    ),
  },
  {
    path: '/tenant/one-click-login',
    errorElement: <RouteErrorFallback />,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <TenantOneClickLogin />
      </Suspense>
    ),
  },
  {
    path: '/wechat-open-platform-authorize',
    errorElement: <RouteErrorFallback />,
    element: (
      <AuthRoute>
        <Suspense fallback={<LoadingComponent />}>
          <WechatOpenPlatformAuthorize />
        </Suspense>
      </AuthRoute>
    ),
  },
  {
    path: '/',
    errorElement: <RouteErrorFallback />,
    element: <RootShell />,
    children: [
      {
        index: true,
        element: <Navigate to={isTenantFrontend ? '/tenant/login' : '/dashboard'} replace />,
      },
      {
        path: 'dashboard',
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <Dashboard />
          </Suspense>
        ),
      },
      {
        path: 'tenants',
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingComponent />}>
              <Tenants />
            </Suspense>
          </SuperAdminRoute>
        ),
      },
      {
        path: 'users',
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <Users />
          </Suspense>
        ),
      },
      {
        path: 'roles',
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <Roles />
          </Suspense>
        ),
      },
      {
        path: 'permissions',
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <Permissions />
          </Suspense>
        ),
      },
      {
        path: 'ssl-certificates',
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <SslCertificates />
          </Suspense>
        ),
      },
      {
        path: 'sms-records',
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <SmsRecords />
          </Suspense>
        ),
      },
      {
        path: 'sms-config',
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <SmsConfig />
          </Suspense>
        ),
      },
      {
        path: 'captcha-config',
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <CaptchaConfig />
          </Suspense>
        ),
      },
      {
        path: 'website-config',
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <PlatformWebsiteConfig />
          </Suspense>
        ),
      },
      {
        path: 'wechat-open-platform-config',
        element: (
          <Suspense fallback={<LoadingComponent />}>
            <WechatOpenPlatformConfig />
          </Suspense>
        ),
      },
      // 租户系统路由
      {
        path: 'tenant',
        children: [
          {
            index: true,
            element: <Navigate to="/tenant/dashboard" replace />,
          },
          {
            path: 'dashboard',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantDashboard />
              </Suspense>
            ),
          },
          {
            path: 'ad-config',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantAdConfig />
              </Suspense>
            ),
          },
          {
            path: 'website-config',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantWebsiteConfig />
              </Suspense>
            ),
          },
          {
            path: 'seo-config',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantSeoConfig />
              </Suspense>
            ),
          },
          {
            path: 'profile',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantProfile />
              </Suspense>
            ),
          },
          {
            path: 'sub-users',
            element: <Navigate to="/tenant/users" replace />,
          },
          {
            path: 'users',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantUsers />
              </Suspense>
            ),
          },
          {
            path: 'roles',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantRoles />
              </Suspense>
            ),
          },
          {
            path: 'articles',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantArticles />
              </Suspense>
            ),
          },
          {
            path: 'comments',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantComments />
              </Suspense>
            ),
          },
          {
            path: 'mini-users',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantMiniUsers />
              </Suspense>
            ),
          },
          {
            path: 'articles/new',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantArticleEditor />
              </Suspense>
            ),
          },
          {
            path: 'articles/:id',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantArticleEditor />
              </Suspense>
            ),
          },
          {
            path: 'categories',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantCategories />
              </Suspense>
            ),
          },
          {
            path: 'banners',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantBanners />
              </Suspense>
            ),
          },
          {
            path: 'title-library',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantTitleLibrary />
              </Suspense>
            ),
          },
          {
            path: 'auto-writing-workflows',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantAutoWritingWorkflows />
              </Suspense>
            ),
          },
          {
            path: 'writing-stats',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantWritingStats />
              </Suspense>
            ),
          },
          {
            path: 'mini-program-dev',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantMiniProgramDev />
              </Suspense>
            ),
          },
          {
            path: 'wechat-pay-config',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantWechatPayConfig />
              </Suspense>
            ),
          },
          {
            path: 'alipay-config',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantAlipayConfig />
              </Suspense>
            ),
          },
          {
            path: 'qiniu-config',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantQiniuConfig />
              </Suspense>
            ),
          },
          {
            path: 'deepseek-config',
            element: (
              <Suspense fallback={<LoadingComponent />}>
                <TenantDeepSeekConfig />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to={isTenantFrontend ? '/' : '/dashboard'} replace />,
  },
]);

export default router;
